import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Modal, Select } from "barte-design-system";
import { CreditCardIcon } from "@phosphor-icons/react";

import { InputNumber } from "@shared/components";

import type { DelayOption, ModalEditCreditSimpleProps } from "./modal-edit-credit-simple.type";
import styles from "./modal-edit-credit-simple.module.css";

const buildDelayOptions = (delay: number | null, isInvalid: boolean, delayOptions: DelayOption[], t: TFunction): DelayOption[] =>
    isInvalid && delay !== null
        ? [{ value: String(delay), label: `D + ${delay} (${t("payfac.pageRatesDelayInvalidLabel")})`, disabled: true }, ...delayOptions]
        : delayOptions;

const DelaySelectField = ({ label, delay, isInvalid, onChange, disabled, delayOptions, t }: {
    label: string;
    delay: number | null;
    isInvalid: boolean;
    onChange: (value: string) => void;
    disabled?: boolean;
    delayOptions: DelayOption[];
    t: TFunction;
}) => (
    <>
        <Select
            label={label}
            options={buildDelayOptions(delay, isInvalid, delayOptions, t)}
            value={delay !== null ? String(delay) : ""}
            onChange={onChange}
            disabled={disabled}
            hidePrefixIcon
        />
        {isInvalid && (
            <span className={styles["delay-invalid-message"]}>
                {t("payfac.pageRatesDelayInvalidError")}
            </span>
        )}
    </>
);

export const ModalEditCreditSimple = ({
    isOpen,
    onClose,
    creditType,
    sellerRates,
    buyerRates,
    delayForRedeem,
    sightCreditDelay,
    sightCreditRate,
    receiptType,
    availableDelays = [],
    isLoading,
    onHandleSubmit,
}: ModalEditCreditSimpleProps) => {
    const { t } = useTranslation();

    const [formSellerRates, setFormSellerRates] = useState<(number | undefined)[]>(sellerRates);
    const [formBuyerRates, setFormBuyerRates] = useState<(number | undefined)[]>(buyerRates);
    const [formDelay, setFormDelay] = useState<number | null>(delayForRedeem ?? null);
    const [formSightDelay, setFormSightDelay] = useState<number | null>(sightCreditDelay ?? null);
    const [formSightRate, setFormSightRate] = useState<number | null>(sightCreditRate ?? null);
    const [formReceiptType, setFormReceiptType] = useState<string>(receiptType ?? "EARLY");
    const [pendingAction, setPendingAction] = useState<{ type: "receiptType" | "delay" | "restoreReceiptType" | "restoreDelay"; value: string | number | null } | null>(null);

    useEffect(() => {
        if (isOpen) {
            setFormSellerRates(sellerRates);
            setFormBuyerRates(buyerRates);
            setFormDelay(delayForRedeem ?? null);
            setFormSightDelay(sightCreditDelay ?? null);
            setFormSightRate(sightCreditRate ?? null);
            setFormReceiptType(receiptType ?? "EARLY");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const hasRatesFilled = formSellerRates.some((v) => v !== undefined) || formBuyerRates.some((v) => v !== undefined);
    const originalReceiptType = receiptType ?? "EARLY";
    const originalDelay = delayForRedeem ?? null;

    const clearRates = (newReceiptType: string, newDelay: number | null) => {
        setFormReceiptType(newReceiptType);
        setFormDelay(newDelay);
        setFormSightDelay(newDelay);
        setFormSightRate(null);
        setFormSellerRates(Array(sellerRates.length).fill(undefined));
        setFormBuyerRates(Array(buyerRates.length).fill(undefined));
        setPendingAction(null);
    };

    const restoreRates = (newReceiptType: string, newDelay: number | null) => {
        setFormReceiptType(newReceiptType);
        setFormDelay(newDelay);
        setFormSightDelay(sightCreditDelay ?? null);
        setFormSightRate(sightCreditRate ?? null);
        setFormSellerRates([...sellerRates]);
        setFormBuyerRates([...buyerRates]);
        setPendingAction(null);
    };

    const handleReceiptTypeChange = (value: string) => {
        if (value === formReceiptType) return;
        if (value === originalReceiptType) {
            setPendingAction({ type: "restoreReceiptType", value });
        } else if (hasRatesFilled) {
            setPendingAction({ type: "receiptType", value });
        } else {
            clearRates(value, value === "TIMELINE" ? 30 : null);
        }
    };

    const delayOptions = availableDelays.map((d) => ({ value: String(d), label: `D + ${d}` }));

    const isInstallmentDelayInvalid = delayOptions.length > 0 && formDelay !== null && !availableDelays.includes(formDelay);
    const isSightDelayInvalid = delayOptions.length > 0 && formReceiptType !== "TIMELINE" && formSightDelay !== null && !availableDelays.includes(formSightDelay);

    const handleDelayChange = (value: string) => {
        const numVal = value ? Number(value) : null;
        if (formReceiptType !== "EARLY") {
            setFormDelay(numVal);
            return;
        }
        if (numVal === originalDelay) {
            setPendingAction({ type: "restoreDelay", value: numVal });
        } else if (hasRatesFilled) {
            setPendingAction({ type: "delay", value: numVal });
        } else {
            setFormDelay(numVal);
        }
    };

    const confirmPendingAction = () => {
        if (!pendingAction) return;
        if (pendingAction.type === "receiptType") {
            const newType = pendingAction.value as string;
            clearRates(newType, newType === "TIMELINE" ? 30 : null);
        } else if (pendingAction.type === "restoreReceiptType") {
            restoreRates(pendingAction.value as string, originalDelay);
        } else if (pendingAction.type === "restoreDelay") {
            restoreRates(formReceiptType, pendingAction.value as number | null);
        } else {
            setFormDelay(pendingAction.value as number | null);
            setFormSellerRates(Array(sellerRates.length).fill(undefined));
            setFormBuyerRates(Array(buyerRates.length).fill(undefined));
            setPendingAction(null);
        }
    };

    const handleSellerRateChange = (index: number, value?: number) => {
        setFormSellerRates((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const handleBuyerRateChange = (index: number, value?: number) => {
        setFormBuyerRates((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const handleSave = () => {
        onHandleSubmit(
            formSellerRates.map((v) => v ?? 0),
            formBuyerRates.map((v) => v ?? 0),
            formDelay,
            formReceiptType,
            formSightDelay,
            formSightRate,
        );
    };

    const hasDiff =
        JSON.stringify(formSellerRates) !== JSON.stringify(sellerRates) ||
        JSON.stringify(formBuyerRates) !== JSON.stringify(buyerRates) ||
        formDelay !== (delayForRedeem ?? null) ||
        formSightDelay !== (sightCreditDelay ?? null) ||
        formSightRate !== (sightCreditRate ?? null) ||
        formReceiptType !== (receiptType ?? "EARLY");

    const isValid =
        !isInstallmentDelayInvalid &&
        !isSightDelayInvalid &&
        formSellerRates.every((v) => v !== undefined) &&
        formBuyerRates.every((v) => v !== undefined) &&
        (formReceiptType === "TIMELINE" || formDelay !== null);

    const creditTypeLabels: Record<string, string> = {
        online: t("payfac.pageRatePlanModalTabOnline"),
        pos: t("payfac.pageRatePlanModalTabPos"),
        tap: t("payfac.pageRatePlanModalTabTap"),
    };

    const title = (
        <div className={styles["modal-header"]}>
            <CreditCardIcon />
            {t("payfac.pageRatesModalEditCreditSimpleTitle")} — {creditTypeLabels[creditType]}
        </div>
    ) as unknown as string;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="lg"
            className={styles.modal}
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: handleSave,
                    disabled: !hasDiff || !isValid,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <div className={styles["receipt-type-field"]}>
                    <Select
                        label={t("payfac.pageRatesReceiptTypeLabel")}
                        options={[
                            { value: "EARLY", label: t("payfac.pageRatesReceiptTypeEarly") },
                            { value: "TIMELINE", label: t("payfac.pageRatesReceiptTypeTimeline") },
                        ]}
                        value={formReceiptType}
                        onChange={handleReceiptTypeChange}
                        hidePrefixIcon
                    />
                </div>

                <div className={styles["sight-credit-section"]}>
                    <span className={styles["section-label"]}>{t("payfac.pageRatesSightCreditTitle")}</span>
                    <div className={styles["sight-credit-fields"]}>
                        <DelaySelectField
                            label={t("payfac.pageRatesRepassTimeSight")}
                            delay={formSightDelay}
                            isInvalid={isSightDelayInvalid}
                            onChange={(value) => setFormSightDelay(value ? Number(value) : null)}
                            disabled={formReceiptType === "TIMELINE"}
                            delayOptions={delayOptions}
                            t={t}
                        />
                        <InputNumber
                            label={t("payfac.pageRatesTableColumnSellerRate")}
                            value={formSightRate}
                            onChange={(value) => setFormSightRate(value ?? null)}
                        />
                    </div>
                </div>

                <div className={styles["installments-section"]}>
                    <span className={styles["section-label"]}>{t("payfac.pageRatesCreditInstallmentsTitle")}</span>
                    <div className={styles["delay-field"]}>
                        <DelaySelectField
                            label={t("payfac.pageRatesRepassTimeInstallments")}
                            delay={formDelay}
                            isInvalid={isInstallmentDelayInvalid}
                            onChange={handleDelayChange}
                            disabled={formReceiptType === "TIMELINE"}
                            delayOptions={delayOptions}
                            t={t}
                        />
                    </div>

                    <div className={styles.table}>
                        <div className={styles["table-header"]}>
                            <span>{t("payfac.pageRatesTableColumnInstallment")}</span>
                            <span>{t("payfac.pageRatesTableColumnBuyerRate")}</span>
                            <span>{t("payfac.pageRatesTableColumnSellerRate")}</span>
                        </div>

                        <div className={styles["table-body"]}>
                            {formSellerRates.map((rate, index) => (
                                <div key={index} className={styles["table-row"]}>
                                    <span>{index + 2}x</span>
                                    <InputNumber
                                        value={formBuyerRates[index]}
                                        onChange={(value) => handleBuyerRateChange(index, value)}
                                    />
                                    <InputNumber
                                        value={rate}
                                        onChange={(value) => handleSellerRateChange(index, value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={!!pendingAction}
                title={t("payfac.pageRatesClearRatesWarningTitle")}
                onClose={() => setPendingAction(null)}
                size="sm"
                footer={{
                    primaryButton: {
                        children: t("payfac.pageRatesClearRatesWarningConfirm"),
                        onClick: confirmPendingAction,
                    },
                    secondaryButton: {
                        children: t("payfac.pageRatesClearRatesWarningCancel"),
                        variant: "outline",
                        onClick: () => setPendingAction(null),
                    },
                    buttonLayout: "hug",
                }}
            >
                <p>
                    {pendingAction?.type === "receiptType"
                        ? t("payfac.pageRatesClearRatesWarningReceiptType")
                        : pendingAction?.type === "delay"
                            ? t("payfac.pageRatesClearRatesWarningDelay")
                            : pendingAction?.type === "restoreReceiptType"
                                ? t("payfac.pageRatesRestoreRatesWarningReceiptType")
                                : t("payfac.pageRatesRestoreRatesWarningDelay")}
                </p>
            </Modal>
        </Modal>
    );
};
